import { FamilyMember, InvestmentFolio, Portfolio, Expense, Income, FinancialSummary } from '../types';
import authService from './auth';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

class ApiService {
  private async request<T>(endpoint: string, options?: RequestInit): Promise<T> {
    const url = `${API_BASE_URL}${endpoint}`;
    
    const headers = authService.getAuthHeaders();
    
    const config: RequestInit = {
      headers: {
        ...headers,
        ...(options?.headers || {}),
      },
      ...options,
    };
    
    // Remove Content-Type if it's FormData (for file uploads)
    if (options?.body instanceof FormData) {
      delete (config.headers as any)['Content-Type'];
    }

    try {
      const response = await fetch(url, config);
      
      if (!response.ok) {
        // Handle 401/403 unauthorized - redirect to login
        if (response.status === 401 || response.status === 403) {
          authService.logout();
          window.location.reload(); // Reload to show login page
          throw new Error('Unauthorized - please login again');
        }
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        throw new Error(errorData.error || errorData.message || `HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error(`API request failed: ${endpoint}`, error);
      throw error;
    }
  }

  // Health check
  async healthCheck(): Promise<{ status: string; message: string }> {
    return this.request('/health');
  }

  // Family Members
  async getFamilyMembers(): Promise<FamilyMember[]> {
    return this.request('/family-members');
  }

  async createFamilyMember(member: Omit<FamilyMember, '_id' | 'createdAt' | 'updatedAt'>): Promise<FamilyMember> {
    return this.request('/family-members', {
      method: 'POST',
      body: JSON.stringify(member),
    });
  }

  async updateFamilyMember(id: string, member: Partial<FamilyMember>): Promise<FamilyMember> {
    return this.request(`/family-members/${id}`, {
      method: 'PUT',
      body: JSON.stringify(member),
    });
  }

  async deleteFamilyMember(id: string): Promise<{ message: string }> {
    return this.request(`/family-members/${id}`, {
      method: 'DELETE',
    });
  }

  // Investments
  async getInvestments(): Promise<InvestmentFolio[]> {
    return this.request('/investments');
  }

  async createInvestment(investment: Omit<InvestmentFolio, '_id' | 'createdAt' | 'updatedAt'>): Promise<InvestmentFolio> {
    return this.request('/investments', {
      method: 'POST',
      body: JSON.stringify(investment),
    });
  }

  async updateInvestment(id: string, investment: Partial<InvestmentFolio>): Promise<InvestmentFolio> {
    return this.request(`/investments/${id}`, {
      method: 'PUT',
      body: JSON.stringify(investment),
    });
  }

  async deleteInvestment(id: string): Promise<{ message: string }> {
    return this.request(`/investments/${id}`, {
      method: 'DELETE',
    });
  }

  async transferInvestment(id: string, newFamilyMemberId: string): Promise<InvestmentFolio> {
    return this.request(`/investments/${id}/transfer`, {
      method: 'PATCH',
      body: JSON.stringify({ newFamilyMemberId }),
    });
  }

  // Portfolio
  async getPortfolioSummary(): Promise<{
    familyMembers: FamilyMember[];
    investments: InvestmentFolio[];
    summary: {
      totalInvested: number;
      totalCurrentValue: number;
      totalGrowth: number;
      growthPercentage: number;
      totalFamilyMembers: number;
      totalInvestments: number;
    };
  }> {
    return this.request('/portfolio/summary');
  }

  // Expenses
  async getExpenses(filters?: { familyMemberId?: string; type?: string; category?: string }): Promise<Expense[]> {
    const query = new URLSearchParams(filters).toString();
    return this.request(`/expenses${query ? `?${query}` : ''}`);
  }

  async getExpenseById(id: string): Promise<Expense> {
    return this.request(`/expenses/${id}`);
  }

  async createExpense(expense: Omit<Expense, '_id' | 'createdAt' | 'updatedAt'>): Promise<Expense> {
    return this.request('/expenses', {
      method: 'POST',
      body: JSON.stringify(expense),
    });
  }

  async updateExpense(id: string, expense: Partial<Expense>): Promise<Expense> {
    return this.request(`/expenses/${id}`, {
      method: 'PUT',
      body: JSON.stringify(expense),
    });
  }

  async deleteExpense(id: string): Promise<{ message: string }> {
    return this.request(`/expenses/${id}`, {
      method: 'DELETE',
    });
  }

  async toggleExpenseStatus(id: string, isActive: boolean): Promise<Expense> {
    return this.request(`/expenses/${id}/toggle-status`, {
      method: 'PATCH',
      body: JSON.stringify({ isActive }),
    });
  }

  async getExpenseSummary(): Promise<{
    monthlyRecurring: number;
    oneOffTotal: number;
    totalExpenses: number;
    activeExpenses: number;
    byCategory: Record<string, { total: number; count: number }>;
    byType: Record<string, { total: number; count: number }>;
  }> {
    return this.request('/expenses/summary');
  }

  // Income
  async getIncomes(filters?: { familyMemberId?: string; type?: string; frequency?: string }): Promise<Income[]> {
    const query = new URLSearchParams(filters).toString();
    return this.request(`/incomes${query ? `?${query}` : ''}`);
  }

  async getIncomeById(id: string): Promise<Income> {
    return this.request(`/incomes/${id}`);
  }

  async createIncome(income: Omit<Income, '_id' | 'createdAt' | 'updatedAt'>): Promise<Income> {
    return this.request('/incomes', {
      method: 'POST',
      body: JSON.stringify(income),
    });
  }

  async updateIncome(id: string, income: Partial<Income>): Promise<Income> {
    return this.request(`/incomes/${id}`, {
      method: 'PUT',
      body: JSON.stringify(income),
    });
  }

  async deleteIncome(id: string): Promise<{ message: string }> {
    return this.request(`/incomes/${id}`, {
      method: 'DELETE',
    });
  }

  async toggleIncomeStatus(id: string, isActive: boolean): Promise<Income> {
    return this.request(`/incomes/${id}/toggle-status`, {
      method: 'PATCH',
      body: JSON.stringify({ isActive }),
    });
  }

  async getIncomeSummary(): Promise<{
    monthlyTotal: number;
    yearlyTotal: number;
    oneTimeTotal: number;
    totalIncomes: number;
    activeIncomes: number;
    byType: Record<string, { total: number; monthlyTotal: number; count: number }>;
    byFamilyMember: Record<string, { total: number; monthlyTotal: number; count: number }>;
  }> {
    return this.request('/incomes/summary');
  }

  // Financial Summary
  async getFinancialSummary(): Promise<{
    totalMonthlyIncome: number;
    totalMonthlyExpenses: number;
    monthlySurplus: number;
    surplusPercentage: number;
    incomeCount: number;
    expenseCount: number;
  }> {
    return this.request('/financial-summary');
  }

  // Data Migration
  async migrateData(data: Portfolio): Promise<{ message: string; migrated: { familyMembers: number; investments: number } }> {
    return this.request('/migrate', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // CAS Upload
  async uploadCasPdf(formData: FormData): Promise<any> {
    const url = `${API_BASE_URL}/cas/upload`;
    const token = authService.getToken();
    
    try {
      const headers: HeadersInit = {};
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      const response = await fetch(url, {
        method: 'POST',
        headers,
        body: formData, // Don't set Content-Type header for FormData
      });

      if (!response.ok) {
        // Handle 401 unauthorized - redirect to login
        if (response.status === 401) {
          authService.logout();
          window.location.reload(); // Reload to show login page
        }
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        throw new Error(errorData.error || errorData.message || `HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('CAS upload error:', error);
      throw error;
    }
  }
}

export const apiService = new ApiService();
export default apiService;
